import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiFetch } from '@/lib/api';
import { ArrowRight, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TableLink } from '@/components/global';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: 'HUB' | 'SPOKE';
}

interface Transfer {
  id: string;
  transferNumber: string;
  transferDate: string;
  status: string;
  totalItems: number;
  totalQuantity: number;
  fromWarehouse?: Warehouse;
  toWarehouse?: Warehouse;
}

interface Venue {
  id: string;
  name: string;
  parentWarehouseId?: string | null;
}

interface PaginatedResult {
  items: Transfer[];
  total: number;
  page: number;
  pageSize: number;
}

const STATUS_OPTIONS = ['ALL', 'DRAFT', 'APPROVED', 'SHIPPED', 'RECEIVED', 'CANCELLED'];

function resolveSpokeWarehouseId(venue: Venue, spokes: Warehouse[]): string | null {
  if (venue.parentWarehouseId) {
    return venue.parentWarehouseId;
  }
  const exact = spokes.find((s) => s.name === `Spoke — ${venue.name}`);
  if (exact) return exact.id;
  const partial = spokes.find(
    (s) => s.name.endsWith(venue.name) || s.name.includes(venue.name)
  );
  return partial?.id ?? null;
}

const statusStyles: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  SHIPPED: 'bg-yellow-100 text-yellow-800',
  RECEIVED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
};

export default function TransfersList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'ALL');
  const [dateFrom, setDateFrom] = useState(searchParams.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('dateTo') || '');
  const [venueFilter, setVenueFilter] = useState(searchParams.get('venueId') || 'all');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [spokes, setSpokes] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiFetch('/api/v1/venues?pageSize=500'),
      apiFetch('/api/v1/warehouses?type=SPOKE&pageSize=500'),
    ]).then(async ([venueRes, spokeRes]) => {
      if (venueRes.ok) {
        const data = await venueRes.json();
        setVenues(data.items ?? data ?? []);
      }
      if (spokeRes.ok) {
        const data = await spokeRes.json();
        setSpokes(data.items ?? data ?? []);
      }
    });
  }, []);

  const venueSpokeId =
    venueFilter === 'all'
      ? null
      : resolveSpokeWarehouseId(
          venues.find((v) => v.id === venueFilter) ?? { id: venueFilter, name: '' },
          spokes
        );

  const loadTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'ALL') {
        params.set('status', statusFilter);
      }
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (venueSpokeId) params.set('toWarehouseId', venueSpokeId);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));

      const res = await apiFetch(`/api/v1/transfers?${params}`);
      if (res.ok) {
        const data: PaginatedResult = await res.json();
        setTransfers(data.items);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error loading transfers:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo, venueSpokeId, page, pageSize]);

  useEffect(() => {
    loadTransfers();
  }, [loadTransfers]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'ALL') params.set('status', statusFilter);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (venueFilter !== 'all') params.set('venueId', venueFilter);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params);
  }, [statusFilter, dateFrom, dateTo, venueFilter, page, setSearchParams]);

  const filterableVenues = venues.filter((v) => resolveSpokeWarehouseId(v, spokes) !== null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Stock Transfers</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Transfer documents between locations.
            </p>
          </div>
          <Button onClick={() => navigate('/locations/transfers/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Transfer
          </Button>
        </div>

        <Card className="mb-6">
          <CardContent className="grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label>Status</Label>
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status === 'ALL' ? 'All Statuses' : status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Venue</Label>
              <Select
                value={venueFilter}
                onValueChange={(v) => {
                  setVenueFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Venues" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Venues</SelectItem>
                  {filterableVenues.map((venue) => (
                    <SelectItem key={venue.id} value={venue.id}>
                      {venue.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div>
              <Label>To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Transfer #</TableHead>
                <TableHead className="hidden sm:table-cell">Date</TableHead>
                <TableHead>From → To</TableHead>
                <TableHead className="hidden text-right md:table-cell">Items</TableHead>
                <TableHead className="hidden text-right md:table-cell">Quantity</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : transfers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No transfers found
                  </TableCell>
                </TableRow>
              ) : (
                transfers.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>
                      <TableLink to={`/locations/transfers/${t.id}`}>
                        {t.transferNumber}
                      </TableLink>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">{new Date(t.transferDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <span>{t.fromWarehouse?.name || '-'}</span>
                        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>{t.toWarehouse?.name || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden text-right md:table-cell">{t.totalItems}</TableCell>
                    <TableCell className="hidden text-right md:table-cell">{t.totalQuantity}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${statusStyles[t.status] || 'bg-muted'}`}>
                        {t.status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between p-4 border-t">
            <div className="text-sm text-muted-foreground">
              Page {page} of {totalPages} • {total} total
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
